/** Types for the Alation tab: stored-credential summary and connection-test result. */

/** What `GET /alation/config` returns. `configured: false` means no credentials are stored. */
export type AlationConfig = {
  configured: boolean;
  base_url: string | null;
  client_id_masked: string | null;
  last_verified_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

/** Outcome of a live handshake against the Alation MCP server. */
export type AlationConnectionTest = {
  connected: boolean;
  server_name: string | null;
  server_version: string | null;
  tool_count: number;
  /** Tools the read-only policy admits. */
  available_tool_count: number;
};

/** The credential form. `clientSecret` is write-only and never populated from the server. */
export type AlationConfigDraft = {
  baseUrl: string;
  clientId: string;
  clientSecret: string;
};

export const emptyAlationConfigDraft: AlationConfigDraft = {
  baseUrl: "",
  clientId: "",
  clientSecret: "",
};

export function isAlationConfig(value: unknown): value is AlationConfig {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.configured === "boolean";
}

export function isAlationConnectionTest(value: unknown): value is AlationConnectionTest {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.connected === "boolean" && typeof row.available_tool_count === "number";
}

/**
 * Counts from the last completed catalog sync. All zero before the first run.
 *
 * One key per semantic-model section the sync can write, so "the sync ran and changed nothing"
 * is distinguishable from "the sync ran and the tab only reports the three sections it used to".
 */
export type AlationSyncSummary = {
  entities_updated: number;
  entities_created: number;
  fields_updated: number;
  fields_created: number;
  glossary_added: number;
  glossary_updated: number;
  proposals_staged: number;
  tables_matched: number;
  tables_unmatched: number;
  terms_matched: number;
  measures_added: number;
  relationships_added: number;
  rules_added: number;
  examples_added: number;
  ontologies_added: number;
  dimensions_added: number;
  synonyms_added: number;
  aliases_added: number;
};

/**
 * What `GET /alation/semantic-sync` returns.
 *
 * `configured` repeats what `/config` reports so the Enrichment tab can gate its menu item on one
 * poll rather than two calls; `running` is reconciled server-side against the job queue, so a run
 * whose worker died reads `failed` rather than spinning here forever.
 */
export type AlationSemanticSyncStatus = {
  domain_id: number;
  configured: boolean;
  running: boolean;
  status: string | null;
  job_id: string | null;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  summary: AlationSyncSummary;
  /** Selected tables Alation has no catalog entry for. */
  unmatched_tables: string[];
};

export function isAlationSemanticSyncStatus(value: unknown): value is AlationSemanticSyncStatus {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.configured === "boolean" && typeof row.running === "boolean"
  );
}
